import type { StatusMessageValue } from "@/components/base/StatusMessage";

/**
 * 떠 있는 알림(토스트)의 보관소 (2026-10-02).
 *
 * **왜 스토어인가 — 알림의 수명을 발행자와 끊기 위해서다.**
 * `StatusMessage` 가 제자리에서 `createPortal` 하면 그 노드가 언마운트될 때 알림도 함께
 * 죽는다. 그런데 성공하면 닫히는 모달·패널·폼 안에 `StatusMessage` 가 있는 자리가 아홉
 * 곳이다(`QuickCreatePanel`·`VehiclePanel`·`PortalAccountPanel`·`master/plan` 두 모달·
 * `CreateForm`·`LinkableMesRecords`·`ValidationDetail`·`driver/profile` 비밀번호 폼).
 * 거기서는 **성공 알림이 아예 보이지 않는 회귀**가 된다 — `driver/profile` 의 `done`
 * 플래그가 그 문제를 손으로 때운 흔적이었고, 거두는 코드가 없어 영구히 남았다.
 *
 * 그래서 발행만 컴포넌트가 하고, 항목은 여기가 들고 있다가 스스로 거둔다.
 *
 * **자동 소멸은 톤으로 가른다**(담당자 선택 2026-10-02).
 * `success`·`info` 는 4초 뒤 사라지고, `warn`·`error` 는 `닫기` 를 누를 때까지 남는다.
 * `warn` 을 자동에 넣지 않는 이유 — `useStatusMessage.ok(text, warnings)` 는 경고가 딸려
 * 오면 톤을 `warn` 으로 내린다. 그건 *"됐지만 확인할 것이 남았다"* 라 저절로 사라지면
 * 담당자가 짚어야 할 것을 못 본다.
 *
 * 타이머는 `components/base/usePendingCommit.ts` 의 방식을 따른다 — `window.setTimeout`
 * 과 해제를 짝으로 두고, 교체·제거할 때 먼저 거둔다.
 */

/** 성공·안내가 스스로 사라지는 데 걸리는 시간 */
const AUTO_DISMISS_MS = 4_000;

export interface ToastEntry {
  /** 발행한 렌더 지점의 `useId()`. 같은 자리의 재발행은 쌓지 않고 **교체**한다 */
  id: string;
  value: StatusMessageValue;
  /** 발행 순서 — 레이어가 뜬 순서대로 쌓는다 */
  seq: number;
}

const entries = new Map<string, ToastEntry>();
const timers = new Map<string, number>();
const listeners = new Set<() => void>();

let seq = 0;
/**
 * `useSyncExternalStore` 는 스냅샷이 같은 참조이기를 요구한다. 바뀔 때만 새 배열을 만든다
 * — 매번 `Array.from` 하면 무한 렌더로 간다.
 */
let snapshot: ToastEntry[] = [];

function rebuild() {
  snapshot = Array.from(entries.values()).sort((a, b) => a.seq - b.seq);
  listeners.forEach((listener) => listener());
}

function clearTimer(id: string) {
  const timer = timers.get(id);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timers.delete(id);
  }
}

/**
 * 한 렌더 지점의 알림을 올린다. `value` 가 `null` 이면 내린다 — `msg.clear()` 가
 * 그 길로 들어온다.
 */
export function publish(id: string, value: StatusMessageValue | null | undefined) {
  clearTimer(id);

  if (!value || (!value.text && !value.warnings?.length)) {
    if (entries.delete(id)) rebuild();
    return;
  }

  seq += 1;
  entries.set(id, { id, value, seq });

  if (value.tone === "success" || value.tone === "info") {
    timers.set(
      id,
      window.setTimeout(() => {
        timers.delete(id);
        if (entries.delete(id)) rebuild();
      }, AUTO_DISMISS_MS)
    );
  }

  rebuild();
}

/** `닫기` — 담당자가 직접 내린다 */
export function dismiss(id: string) {
  clearTimer(id);
  if (entries.delete(id)) rebuild();
}

/**
 * 전부 내린다. 화면을 옮길 때 레이어가 부른다 — 남은 실패 알림이 다른 화면까지
 * 따라가면 그 화면에서 누른 결과로 읽힌다.
 */
export function clearAll() {
  if (entries.size === 0) return;
  timers.forEach((timer) => window.clearTimeout(timer));
  timers.clear();
  entries.clear();
  rebuild();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): ToastEntry[] {
  return snapshot;
}

/** 서버 렌더는 쓰지 않지만 `useSyncExternalStore` 가 요구한다 */
export function getServerSnapshot(): ToastEntry[] {
  return [];
}
